import { createAsyncThunk, createSlice, isPending, isRejected } from '@reduxjs/toolkit';
import axios from 'axios';

import { IAdminBusinessSubscription, SubscriptionStatus } from 'app/shared/model/admin-business-subscription.model';
import { IAdminPaymentHistory } from 'app/shared/model/admin-payment-history.model';
import { ISubscriptionStats } from 'app/shared/model/subscription-stats.model';
import { ISubscriptionTrialSettings } from 'app/shared/model/subscription-trial-settings.model';
import { serializeAxiosError } from 'app/shared/reducers/reducer.utils';

const initialState = {
  loading: false,
  errorMessage: null as string | null,
  subscriptions: [] as readonly IAdminBusinessSubscription[],
  totalItems: 0,

  loadingPaymentHistory: false,
  paymentHistory: [] as readonly IAdminPaymentHistory[],
  paymentHistoryTotalItems: 0,

  loadingStats: false,
  stats: null as ISubscriptionStats | null,

  loadingSettings: false,
  updatingSettings: false,
  updateSettingsSuccess: false,
  settingsErrorMessage: null as string | null,
  trialSettings: null as ISubscriptionTrialSettings | null,
};

export type AdminSubscriptionsState = Readonly<typeof initialState>;

const adminUrl = 'api/admin/subscription';

interface FetchParams {
  page: number;
  size: number;
  sort: string;
  /** Optional RSQL filter (status/business/payer, built on the client). */
  query?: string;
}

// Timora backend exposes the admin subscription list as an RSQL filter endpoint returning a
// GenericPaginationResponse ({ data, pagination }) — same contract as businesses/user-management.
export const getSubscriptions = createAsyncThunk(
  'adminSubscriptions/fetch_subscriptions',
  async ({ page, size, sort, query }: FetchParams, { signal }) => {
    const q = query ? `&q=${encodeURIComponent(query)}` : '';
    // Stable PK tiebreaker so rows don't reshuffle across edits when the primary sort has ties (see businesses reducer).
    const requestUrl = `${adminUrl}/filter?page=${page}&size=${size}&sort=${sort}&sort=id,desc${q}`;
    return axios.get<{ data: IAdminBusinessSubscription[]; pagination: { totalItems: number } }>(requestUrl, { signal });
  },
  { serializeError: serializeAxiosError },
);

export const getPaymentHistory = createAsyncThunk(
  'adminSubscriptions/fetch_payment_history',
  async ({ page, size, sort, query }: FetchParams, { signal }) => {
    const q = query ? `&q=${encodeURIComponent(query)}` : '';
    const requestUrl = `${adminUrl}/payment-history/filter?page=${page}&size=${size}&sort=${sort}&sort=id,desc${q}`;
    return axios.get<{ data: IAdminPaymentHistory[]; pagination: { totalItems: number } }>(requestUrl, { signal });
  },
  { serializeError: serializeAxiosError },
);

export const getStats = createAsyncThunk(
  'adminSubscriptions/fetch_stats',
  async (_: void, { signal }) => axios.get<ISubscriptionStats>(`${adminUrl}/stats`, { signal }),
  { serializeError: serializeAxiosError },
);

export const getTrialSettings = createAsyncThunk(
  'adminSubscriptions/fetch_trial_settings',
  async (_: void, { signal }) => axios.get<ISubscriptionTrialSettings>(`${adminUrl}/settings`, { signal }),
  { serializeError: serializeAxiosError },
);

export const updateTrialSettings = createAsyncThunk(
  'adminSubscriptions/update_trial_settings',
  async (body: { registrationTrialDays: number; wompiTrialDays: number }) =>
    axios.put<ISubscriptionTrialSettings>(`${adminUrl}/settings`, body),
  { serializeError: serializeAxiosError },
);

// Support/QA manual override (PATCH /api/admin/subscription/{id}/status) — prod-reachable, ADMIN-only. Forces
// any status regardless of the current one; returns the same enriched row shape as /filter.
export const forceSubscriptionStatus = createAsyncThunk(
  'adminSubscriptions/force_status',
  async ({ id, status }: { id: number; status: SubscriptionStatus }) =>
    axios.patch<IAdminBusinessSubscription>(`${adminUrl}/${id}/status`, { status }),
  { serializeError: serializeAxiosError },
);

export const AdminSubscriptionsSlice = createSlice({
  name: 'adminSubscriptions',
  initialState: initialState as AdminSubscriptionsState,
  reducers: {
    reset() {
      return initialState;
    },
    resetUpdateSettingsSuccess(state) {
      state.updateSettingsSuccess = false;
    },
  },
  extraReducers(builder) {
    builder
      .addCase(getSubscriptions.fulfilled, (state, action) => {
        state.loading = false;
        state.subscriptions = action.payload.data.data;
        state.totalItems = action.payload.data.pagination.totalItems;
      })
      .addCase(forceSubscriptionStatus.fulfilled, (state, action) => {
        const updated = action.payload.data;
        state.subscriptions = state.subscriptions.map(s => (s.id === updated.id ? updated : s));
      })
      .addCase(getPaymentHistory.pending, state => {
        state.loadingPaymentHistory = true;
      })
      .addCase(getPaymentHistory.fulfilled, (state, action) => {
        state.loadingPaymentHistory = false;
        state.paymentHistory = action.payload.data.data;
        state.paymentHistoryTotalItems = action.payload.data.pagination.totalItems;
      })
      .addCase(getPaymentHistory.rejected, (state, action) => {
        if (action.meta?.aborted) return;
        state.loadingPaymentHistory = false;
      })
      .addCase(getStats.pending, state => {
        state.loadingStats = true;
      })
      .addCase(getStats.fulfilled, (state, action) => {
        state.loadingStats = false;
        state.stats = action.payload.data;
      })
      .addCase(getStats.rejected, (state, action) => {
        if (action.meta?.aborted) return;
        state.loadingStats = false;
      })
      .addCase(getTrialSettings.pending, state => {
        state.loadingSettings = true;
        state.settingsErrorMessage = null;
      })
      .addCase(getTrialSettings.fulfilled, (state, action) => {
        state.loadingSettings = false;
        state.trialSettings = action.payload.data;
      })
      .addCase(getTrialSettings.rejected, (state, action) => {
        state.loadingSettings = false;
        state.settingsErrorMessage = action.error.message!;
      })
      .addCase(updateTrialSettings.pending, state => {
        state.updatingSettings = true;
        state.updateSettingsSuccess = false;
        state.settingsErrorMessage = null;
      })
      .addCase(updateTrialSettings.fulfilled, (state, action) => {
        state.updatingSettings = false;
        state.updateSettingsSuccess = true;
        state.trialSettings = action.payload.data;
      })
      .addCase(updateTrialSettings.rejected, (state, action) => {
        state.updatingSettings = false;
        state.updateSettingsSuccess = false;
        state.settingsErrorMessage = action.error.message!;
      })
      .addMatcher(isPending(getSubscriptions), state => {
        state.loading = true;
        state.errorMessage = null;
      })
      .addMatcher(isRejected(getSubscriptions), (state, action) => {
        if (action.meta?.aborted) return; // superseded by a newer request — ignore
        state.loading = false;
        state.errorMessage = action.error.message!;
      });
  },
});

export const { reset, resetUpdateSettingsSuccess } = AdminSubscriptionsSlice.actions;

export default AdminSubscriptionsSlice.reducer;
